import React, { useCallback, useState, useEffect, useMemo } from 'react';
import 'devextreme-react/text-area';
import Form, {
  ButtonItem,
  ButtonOptions,
  RequiredRule,
  CustomRule,
  Item, GroupItem, Label, FormTypes
} from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import { useAuth } from '../../../contexts/auth';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import { WorkTask } from '../../../types/task';
import ODataStore from 'devextreme/data/odata/store';
import axios from 'axios';
import { DropDownBox, TagBox } from 'devextreme-react';
import DataGrid, { Column, DataGridTypes, Scrolling, SearchPanel, Selection } from 'devextreme-react/data-grid';
import { DropDownBoxTypes } from 'devextreme-react/drop-down-box';
import { WORK_TASK_STATUS_LIST } from '../../../shared/constants';
import DataSource from 'devextreme/data/data_source';
import { Relation } from '../../../types/crm-contact';

const WorkTaskForm = React.memo(({ appointmentData, workTaskOid, contractOid, onComplete, allowUnscheduled = false }: { appointmentData: Date[], workTaskOid: string, contractOid: string, onComplete: () => void, allowUnscheduled?: boolean }) => {
  const { user } = useAuth();
  const [workTask, setWorkTask] = useState<Partial<WorkTask>>();
  const [isDropDownOpened, setDropDownOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  // Memoize all ODataStore instances to prevent recreation on every render
  const store = useMemo(() => new ODataStore({
    key: 'Oid',
    keyType: 'Guid',
    url: `${process.env.REACT_APP_URL}/api/odata/WorkTask`,
    version: 4,
    beforeSend: function(e) {
      e.headers = {
        'Authorization': `Bearer ${user?.token}`
      };
    },
  }), [user?.token]);

  const contractDataSource = useMemo(() => new ODataStore({
    key: 'Oid',
    keyType: 'Guid',
    url: `${process.env.REACT_APP_URL}/api/odata/Contract`,
    version: 4,
    beforeSend: function(e) {
      e.headers = {
        'Authorization': `Bearer ${user?.token}`
      };
    },
  }), [user?.token]);

  const activityDataSource = useMemo(() => new ODataStore({
    key: 'Oid',
    keyType: 'Guid',
    url: `${process.env.REACT_APP_URL}/api/odata/Activity`,
    version: 4,
    beforeSend: function(e) {
      e.headers = {
        'Authorization': `Bearer ${user?.token}`
      };
    },
  }), [user?.token]);

  const relationDataSource = useMemo(() => new ODataStore({
    key: 'Oid',
    keyType: 'Guid',
    url: `${process.env.REACT_APP_URL}/api/odata/Relation`,
    version: 4,
    beforeSend: function(e) {
      e.headers = {
        'Authorization': `Bearer ${user?.token}`
      };
    },
  }), [user?.token]);

  const employeesDataSource = useMemo(() => new ODataStore({
    key: 'Oid',
    keyType: 'Guid',
    url: `${process.env.REACT_APP_URL}/api/odata/Employee`,
    version: 4,
    beforeSend: function(e) {
      e.headers = {
        'Authorization': `Bearer ${user?.token}`
      };
    },
  }), [user?.token]);

  const activityData = useMemo(() => new DataSource({ store: activityDataSource }), [activityDataSource]);
  const relationData = useMemo(() => new DataSource({ store: relationDataSource }), [relationDataSource]);
  const employeeData = useMemo(() => new DataSource({ store: employeesDataSource, filter: ['Active', '=', true] }), [employeesDataSource]);

  const getContractData = useCallback(() => {
    if (contractOid) {
      contractDataSource.byKey(contractOid, { expand: ['Relation'] }).then(
        (dataItem) => {
          setWorkTask((prevWorkTask) => ({
            ...prevWorkTask,
            ContractOid: dataItem.Oid,
            Contract: dataItem,
            Relation: dataItem.Relation,
            TaskStartDate: new Date(),
            TaskEndDate: new Date(new Date().getTime() + 30 * 60000), // 30 minutes later
          }));

          // Fetch OH/Service from activityData and set Activity in workTask
          activityData.load().then((activities) => {
            const ohServiceActivity = activities.find((activity) => activity.Name === 'OH/Service');
            if (ohServiceActivity) {
              setWorkTask((prevWorkTask) => ({
                ...prevWorkTask,
                ActivityOid: ohServiceActivity.Oid,
                Activity: ohServiceActivity,
              }));
            }
          });
        }
      );
    }
  }, [contractOid, contractDataSource, activityData]);

  useEffect(() => {
    if (workTaskOid) {
      store.byKey(workTaskOid, { expand: ['Employees', 'Relation', 'CreatedBy', 'Activity'] }).then(
        (dataItem) => {
          // Extract employee OIDs so they are available for updates
          const employeeOids = dataItem.Employees?.map(emp => {
            const oid = emp.Oid;
            return oid?._value?.toString() ?? oid?.toString() ?? oid;
          }) || [];
          // If appointmentData has dates (from drag-and-drop), use those instead of the stored dates
          const hasAppointmentDates = appointmentData && appointmentData[0] && appointmentData[1];
          if (hasAppointmentDates) {
            setWorkTask({
              ...dataItem,
              EmployeeOids: employeeOids,
              TaskStartDate: appointmentData[0],
              TaskEndDate: appointmentData[1]
            });
          } else {
            setWorkTask({
              ...dataItem,
              EmployeeOids: employeeOids,
            });
          }
        }
      );
    } else {
      // For unscheduled tasks, don't set default dates
      const hasAppointmentDates = appointmentData && appointmentData[0] && appointmentData[1];
      setWorkTask({
        TaskStartDate: hasAppointmentDates ? appointmentData[0] : (allowUnscheduled ? undefined : new Date()),
        TaskEndDate: hasAppointmentDates ? appointmentData[1] : (allowUnscheduled ? undefined : new Date()),
        Status: WORK_TASK_STATUS_LIST[0],
        Employees: [], // Initialize empty employees array
        EmployeeOids: [] // Initialize empty employee OIDs array
      });
    }
    getContractData();
  }, [workTaskOid, appointmentData, store, getContractData, allowUnscheduled]);

  const validateForm = useCallback((e: FormTypes.ContentReadyEvent) => {
    e.component.validate();
  }, []);

  const toLocalIso = (date: Date | string | undefined) => {
    if (!date) return undefined;
    const d = date instanceof Date ? date : new Date(date);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const onSubmit = useCallback(async(e) => {
    e.preventDefault();

    // Ask user if they want to send an email to the customer
    const sendEmail = await confirm(
      'Wilt u een e-mailnotificatie naar de klant sturen?',
      'E-mail versturen'
    );

    setLoading(true);
    try {
      if (workTaskOid) {
        await axios.put(`/api/WorkTask/Update/${workTaskOid}`, {
          TaskDescription: workTask?.TaskDescription,
          TaskRemarks: workTask?.TaskRemarks,
          Status: workTask?.Status,
          TaskStartDate: toLocalIso(workTask?.TaskStartDate),
          TaskEndDate: toLocalIso(workTask?.TaskEndDate),
          TaskDone: workTask?.TaskDone,
          ActivityOid: workTask?.ActivityOid ?? workTask?.Activity?.Oid?.toString(),
          RelationOid: workTask?.RelationOid ?? workTask?.Relation?.Oid?.toString(),
          ContractOid: workTask?.ContractOid ?? workTask?.Contract?.Oid?.toString(),
          EmployeeOids: workTask?.EmployeeOids,
          SendEmail: sendEmail,
        }, {
          headers: { 'Authorization': `Bearer ${user?.token}` }
        });
      } else {
        await axios.post('/api/WorkTask/Create', {
          TaskDescription: workTask?.TaskDescription,
          TaskRemarks: workTask?.TaskRemarks,
          Status: workTask?.Status ?? 'New',
          TaskStartDate: toLocalIso(workTask?.TaskStartDate),
          TaskEndDate: toLocalIso(workTask?.TaskEndDate),
          ActivityOid: workTask?.ActivityOid ?? workTask?.Activity?.Oid,
          RelationOid: workTask?.RelationOid ?? workTask?.Relation?.Oid,
          ContractOid: workTask?.ContractOid ?? workTask?.Contract?.Oid,
          EmployeeOids: workTask?.EmployeeOids ?? workTask?.Employees?.map(emp => emp.Oid),
          SendEmail: sendEmail,
        }, {
          headers: { 'Authorization': `Bearer ${user?.token}` }
        });
        if (contractOid) {
          const { patchContract } = await import('../../../api/contracts');
          const cOid = typeof contractOid === 'object' ? (contractOid as any)?._value?.toString() : contractOid?.toString();
          await patchContract(cOid, { MaintenancePlanned: true, LastMaintenanceDate: workTask?.TaskStartDate }, user?.token);
        }
      }
      onComplete();
    } catch (error) {
      notify('Er is een fout opgetreden bij het opslaan van de opdracht', 'error', 1000);
    } finally {
      setLoading(false);
    }
  }, [workTaskOid, workTask, store, contractOid, contractDataSource, onComplete, user?.token]);

  const onEmployeeSelectionChanged = useCallback((e) => {
    // Prevent infinite loops by checking if the value actually changed
    const newEmployeeOids = Array.isArray(e.value) ? e.value : [];

    setWorkTask((prevWorkTask) => {
      const currentOids = prevWorkTask?.Employees?.map(emp => emp.Oid) || [];

      // Check if the employee selection actually changed
      if (JSON.stringify(currentOids.sort()) === JSON.stringify(newEmployeeOids.sort())) {
        return prevWorkTask; // No change, return previous state
      }

      return {
        ...prevWorkTask,
        EmployeeUpdated: true,
        Employees: newEmployeeOids.map((oid) => ({ Oid: oid })),
        EmployeeOids: newEmployeeOids
      };
    });
  }, []);

  const boxOptionChanged = useCallback((e: DropDownBoxTypes.OptionChangedEvent) => {
    if (e.name === 'opened') {
      // Add guard to prevent unnecessary updates
      setDropDownOpened(prevOpened => prevOpened !== e.value ? e.value : prevOpened);
    }
  }, []);

  const onSelectionChanged = useCallback((args: DataGridTypes.SelectionChangedEvent) => {
    if (args.selectedRowKeys && args.selectedRowKeys.length > 0) {
      setWorkTask((prevWorkTask) => ({
        ...prevWorkTask,
        Relation: args.selectedRowsData[0] as Relation,
        RelationOid: args.selectedRowKeys[0] as string
      }));
      // Add guard to prevent unnecessary updates
      setDropDownOpened(prevOpened => prevOpened ? false : prevOpened);
    }
  }, []);

  const formDataChanged = useCallback((e) => {
    setWorkTask((prevWorkTask) => {
      if (e.dataField === 'Activity.Oid') {
        return {
          ...prevWorkTask,
          ActivityOid: e.value._value.toString(),
          Activity: { Oid: e.value._value.toString(), Name: e.component.option('displayValue') }
        } as WorkTask;
      }
      return { ...prevWorkTask, [e.dataField as string]: e.value } as WorkTask;
    });
  }, []);

  const contentRender = useCallback(() => {
    return (
      <DataGrid
        dataSource={relationData}
        selectedRowKeys={workTask && workTask.Relation ? [workTask.Relation.Oid] : []}
        remoteOperations
        height={350}
        keyExpr='Oid'
        hoverStateEnabled
        onSelectionChanged={onSelectionChanged}
        focusedRowEnabled>
        <SearchPanel
          visible
          width={240}
          placeholder='Zoeken...' />
        <Column caption='Nr' width={90} dataField='CustomerNumber' />
        <Column dataField='Name' caption='Naam' sortOrder='asc' />
        <Column caption='Adres'
          calculateCellValue={(row: Record<string, string>) => `${row.Address || ''} ${row.AddressNr || ''}`.trim()} />
        <Column dataField='City' caption='Plaats' />
        <Scrolling mode='virtual' />
        <Selection mode='single' />
      </DataGrid>
    );
  }, [relationData, onSelectionChanged, workTask?.Relation?.Oid]);

  // Memoize editor options to prevent recreation
  const relationEditorOptions = useMemo(() => ({ height: 40, dataSource: relationData, searchEnabled: true, readOnly: false }), [relationData]);
  const activityEditorOptions = useMemo(() => ({ height: 40, dataSource: activityData, searchEnabled: true, disabled: false, valueExpr: 'Oid', displayExpr: 'Name' }), [activityData]);
  const employeeEditorOptions = useMemo(() => ({ height: 40, dataSource: employeeData, searchEnabled: true, disabled: false }), [employeeData]);
  const statusEditorOptions = useMemo(() => ({ height: 40, dataSource: WORK_TASK_STATUS_LIST }), []);
  const notesEditorOptions = useMemo(() => ({ height: 90, maxLength: 200 }), []);
  const dateEditorOptions = useMemo(() => ({ height: 40, type: 'datetime', displayFormat: 'dd-MM-yyyy HH:mm' }), []);

  // Memoize computed values to prevent unnecessary recalculations
  const employeeValues = useMemo(() => {
    const employees = workTask?.Employees;
    if (!employees || !Array.isArray(employees)) {
      return [];
    }
    const values = employees.map((e) => e.Oid).filter(Boolean);
    return values; // Don't sort here to maintain original order
  }, [JSON.stringify(workTask?.Employees?.map(e => e.Oid) || [])]);

  // Memoize static objects to prevent recreation
  const ownerLabel = useMemo(() => ({ 'aria-label': 'Relation' }), []);
  const nameLabel = useMemo(() => ({ 'aria-label': 'Name' }), []);

  // Memoize render functions to prevent recreation
  const renderRelationDropdown = useCallback(() => {
    return (
      <DropDownBox
        placeholder='Kiezen...'
        value={workTask?.Relation?.Oid}
        valueExpr='Oid'
        displayExpr='Name'
        onOptionChanged={boxOptionChanged}
        opened={isDropDownOpened}
        dataSource={relationData}
        inputAttr={ownerLabel}
        contentRender={contentRender} />
    );
  }, [workTask?.Relation?.Oid, boxOptionChanged, isDropDownOpened, relationData, contentRender, ownerLabel]);

  const renderEmployeeTagBoxComponent = useCallback(() => {
    return (
      <TagBox
        dataSource={employeeData}
        value={employeeValues}
        onValueChanged={onEmployeeSelectionChanged}
        valueExpr='Oid'
        displayExpr='FullName'
        showSelectionControls
        maxDisplayedTags={3}
        inputAttr={nameLabel}
        showMultiTagOnly={false}
        applyValueMode='useButtons'
        searchEnabled />
    );
  }, [employeeValues, employeeData, onEmployeeSelectionChanged, nameLabel]);

  return (
    <form onSubmit={onSubmit}>
      <Form formData={workTask} onFieldDataChanged={formDataChanged} onContentReady={validateForm}>
        <GroupItem>
          <Item dataField='Relation.Oid' editorOptions={relationEditorOptions} render={renderRelationDropdown}>
            <Label text='Relatie' />
            <RequiredRule message='Relatie veld is verplicht' />
          </Item>
          <GroupItem colCount={2}>
            <Item dataField='Activity.Oid' editorType='dxSelectBox' editorOptions={activityEditorOptions}>
              <Label text='Werkzaamheden' />
              <RequiredRule message='Werkzaamheden veld is verplicht' />
            </Item>
            <Item dataField='Status' editorType='dxSelectBox' editorOptions={statusEditorOptions}>
              <Label text='Status' />
              <RequiredRule message='Status veld is verplicht' />
            </Item>
          </GroupItem>
          <GroupItem colCount={2}>
            <Item dataField='TaskStartDate' editorOptions={dateEditorOptions} editorType='dxDateBox'>
              <Label text='Start datum' />
              {!allowUnscheduled && <RequiredRule message='Start datum is verplicht' />}
            </Item>
            <Item dataField='TaskEndDate' editorOptions={dateEditorOptions} editorType='dxDateBox'>
              <Label text='Eind datum' />
              {!allowUnscheduled && <RequiredRule message='Eind datum is verplicht' />}
              <CustomRule
                message='Einddatum moet na de startdatum liggen'
                validationCallback={(e) => {
                  const start = workTask?.TaskStartDate ? new Date(workTask.TaskStartDate) : null;
                  const end = e.value ? new Date(e.value) : null;
                  if (!start || !end) return true; // als een van beide leeg is, sla validatie over
                  return end > start;
                }}
              />
            </Item>
          </GroupItem>
          <Item dataField='Employees' editorOptions={employeeEditorOptions} render={renderEmployeeTagBoxComponent}>
            <Label text='Medewerker' />
            {!allowUnscheduled && <RequiredRule message='Medewerker is verplicht' />}
          </Item>
          <Item dataField='TaskDescription' editorOptions={{ height: 40 }}>
            <Label text='Opdracht' />
            <RequiredRule message='Opdracht titel is verplicht' />
          </Item>
          <Item dataField='TaskRemarks' colSpan={2} editorType='dxTextArea' editorOptions={notesEditorOptions}>
            <Label text='Opmerkingen' />
          </Item>
        </GroupItem>
        <ButtonItem>
          <ButtonOptions
            width='25%'
            type='default'
            useSubmitBehavior>
            <span className='dx-button-text'>
              {
                loading
                  ? <LoadIndicator width='24px' height='24px' visible />
                  : 'Opslaan'
              }
            </span>
          </ButtonOptions>
        </ButtonItem>
      </Form>
    </form>
  );
});

export { WorkTaskForm };
